import { useEffect, useRef, useState } from "react";
import { FolderUp, Plus, Star, Upload, X } from "lucide-react";
import {
  Button,
  cn,
  toast,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@flint/ui";
import { useS3Upload, type S3UploadStatus } from "../../../lib/use-s3-upload.ts";
import { trpc } from "../../../lib/trpc.ts";

/** Matches `store-catalog.createInput.images` — the API caps the array at 8. */
const MAX_IMAGES = 8;

/** Tiles per row — the wireframe's `.photo-grid` is `repeat(4, 1fr)`. */
const COLUMNS = 4;

/** "Uploading 3 of 7" while a batch runs; plain "Uploading…" for one file. */
function uploadingLabel(status: S3UploadStatus): string {
  if (status.kind === "uploading" && status.index && status.total) {
    return `Uploading ${status.index} of ${status.total}…`;
  }
  return "Uploading…";
}

/** Keep in sync with `requestImageUploadInput` on the wire. */
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
const MAX_BYTES = 5 * 1024 * 1024;

/** Extension → canonical MIME, for files whose browser-reported type is
 * empty or non-canonical. `File.type` comes from OS/registry mappings, and a
 * folder pick of perfectly valid `.png`s on Windows can yield `""` for every
 * file — filtering on the raw type alone threw whole folders away. */
const EXTENSION_TYPES: Record<string, (typeof ALLOWED_TYPES)[number]> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  jfif: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
};

/** Canonical content type for a picked file, or null when it isn't an image
 * the wire schema accepts. Trust `File.type` when it's already canonical;
 * fall back to the filename extension otherwise. */
function resolveImageType(file: File): (typeof ALLOWED_TYPES)[number] | null {
  const reported = ALLOWED_TYPES.find((t) => t === file.type);
  if (reported) return reported;
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return EXTENSION_TYPES[ext] ?? null;
}

/**
 * No query to invalidate: unlike an avatar, nothing server-side changed yet.
 * The uploaded URL only becomes a record when the product form is saved.
 */
const NO_INVALIDATION: never[] = [];

/**
 * Product images editor — a fixed grid of square slots.
 *
 * Mirrors the `.photo-grid` block in `prototype/store/admin/all.html`: filled
 * slots show the image, empty slots are dashed "+ Add" tiles, and the first
 * image is the primary one. Clicking a tile opens the file picker; clicking a
 * filled one previews it.
 *
 * Entry is by upload only — one file, several, or a whole folder. Feedback goes
 * through toasts like the rest of the app rather than inline banners, so the
 * form doesn't grow its own alert style.
 */
export function ProductImagesField({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const [storageOff, setStorageOff] = useState(false);
  const [preview, setPreview] = useState<number | null>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const full = value.length >= MAX_IMAGES;

  // Direct-to-S3, same three-phase machine the avatar and logo uploaders use.
  // The "confirm" phase is a no-op here: there is no server-side record until
  // the product form is saved, so the presigned URL's `publicUrl` is the
  // result we keep.
  const requestUpload = trpc.storeCatalog.requestImageUpload.useMutation();
  const uploadedUrl = useRef<string | null>(null);
  // Uploads that completed but came back with no `publicUrl` — counted so the
  // batch toast doesn't claim success for an image that will never render.
  const urllessUploads = useRef(0);
  // A batch appends several times without re-rendering in between, so reading
  // `value` from the closure would append to a stale array and keep only the
  // last image. The ref is advanced synchronously as each upload lands.
  const valueRef = useRef(value);
  valueRef.current = value;
  const upload = useS3Upload({
    multiple: true,
    request: {
      mutateAsync: async (input) => {
        // The hook resolved the canonical type pre-flight; the wire schema
        // wants the enum, so this only narrows — but it throws rather than
        // casting blindly.
        const contentType = ALLOWED_TYPES.find((t) => t === input.contentType);
        if (!contentType) throw new Error("Use a JPEG, PNG or WebP image.");
        const res = await requestUpload.mutateAsync({ contentType, size: input.size });
        uploadedUrl.current = res.publicUrl;
        return { uploadUrl: res.uploadUrl, objectKey: res.objectKey };
      },
    },
    confirm: { mutateAsync: async () => ({}) },
    allowedTypes: ALLOWED_TYPES,
    resolveType: resolveImageType,
    maxBytes: MAX_BYTES,
    invalidateQueries: NO_INVALIDATION,
    copy: {
      invalidType: "Use a JPEG, PNG or WebP image.",
      tooLarge: "Image must be under 5 MB.",
    },
    onSuccess: () => {
      const next = uploadedUrl.current;
      uploadedUrl.current = null;
      if (!next) {
        urllessUploads.current += 1;
        return;
      }
      const current = valueRef.current;
      if (current.includes(next) || current.length >= MAX_IMAGES) return;
      const updated = [...current, next];
      valueRef.current = updated;
      onChange(updated);
    },
  });

  /**
   * Take a picked file list (multi-select or a whole folder) and hand the
   * usable images to the uploader.
   *
   * A folder is messy — it carries `.DS_Store`, PDFs, RAWs — so we filter to
   * the image types the wire schema accepts, sort by filename (directory
   * enumeration order isn't guaranteed), and cap at the remaining slots.
   * Whatever is dropped is *reported*, never silently discarded.
   */
  const acceptFiles = async (picked: File[]) => {
    // Resolve the canonical type per file — never the raw `File.type`, which
    // is empty for folder picks whose extensions the OS can't map.
    const images = picked
      .filter((f) => resolveImageType(f) !== null)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    const nonImages = picked.length - images.length;

    const room = MAX_IMAGES - valueRef.current.length;
    if (room <= 0) {
      toast.error(`That's the ${MAX_IMAGES}-image limit`, {
        description: "Remove an image before adding another.",
      });
      return;
    }
    const overCap = Math.max(0, images.length - room);
    const batch = images.slice(0, room);
    if (batch.length === 0) {
      toast.error("Nothing to add", {
        description: "No JPEG, PNG or WebP images in that selection.",
      });
      return;
    }

    urllessUploads.current = 0;
    const result = await upload.uploadFiles(batch);
    // Uploads whose presign returned no publicUrl never render — they are
    // failures as far as the user is concerned, not successes.
    const urlless = urllessUploads.current;
    urllessUploads.current = 0;
    const added = result.uploaded - urlless;
    const failedCount = result.failed.length + urlless;

    // One consolidated report per batch — success and problems in a single
    // toast, never a success and an error toast side by side.
    const notes: string[] = [];
    if (nonImages > 0) notes.push(`${nonImages} not a JPEG, PNG or WebP`);
    if (overCap > 0) notes.push(`${overCap} over the ${MAX_IMAGES}-image limit`);
    if (result.skipped.length > 0) notes.push(`${result.skipped.length} rejected`);
    if (failedCount > 0) notes.push(`${failedCount} failed`);

    if (added > 0) {
      const title = `${added} image${added === 1 ? "" : "s"} added`;
      if (notes.length) {
        toast.success(title, { description: `Skipped: ${notes.join(", ")}.` });
      } else {
        toast.success(title);
      }
    } else {
      const firstFailure = result.failed[0]?.reason;
      toast.error("Couldn't add images", {
        description: firstFailure ?? "No JPEG, PNG or WebP images in that selection.",
      });
    }
  };

  // Batch outcomes (success, skips, failures) are reported once per batch in
  // `acceptFiles` — every upload path funnels through it, so toasting here
  // too produced contradictory success+error pairs.
  //
  // A disabled bucket is a configuration state rather than a failure: latch it
  // once, disable the buttons and explain in the helper line.
  useEffect(() => {
    if (upload.status.kind !== "error") return;
    if (/storage isn't configured|uploads are disabled/i.test(upload.status.message)) {
      setStorageOff(true);
    }
  }, [upload.status]);
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  const makePrimary = (i: number) => {
    const copy = [...value];
    const [picked] = copy.splice(i, 1);
    if (picked) onChange([picked, ...copy]);
  };

  // A FIXED grid of whole rows — uploading fills a slot instead of appending a
  // tile, so the section doesn't grow taller with every image. Matches
  // `.photo-grid { repeat(4, 1fr) }` in the wireframe. Past 4 images we pad to
  // the next whole row rather than leaving a ragged edge.
  const rows = Math.max(1, Math.ceil(value.length / COLUMNS));
  const emptySlots = Math.min(rows * COLUMNS, MAX_IMAGES) - value.length;

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {value.map((src, i) => (
          <div key={`${src}-${i}`} className="group relative aspect-square">
            <button
              type="button"
              onClick={() => setPreview(i)}
              title="Preview image"
              aria-label={`Preview image ${i + 1} of ${value.length}`}
              className="block h-full w-full cursor-zoom-in rounded-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-sky-100"
            >
              <img
                src={src}
                alt=""
                className="h-full w-full rounded-sm border border-line object-cover"
              />
            </button>
            {i === 0 ? (
              <span className="absolute left-1 top-1 rounded-full border border-sky-100 bg-paper px-1.5 py-0.5 text-xs font-medium text-sky-700">
                Primary
              </span>
            ) : null}
            <div className="absolute right-1 top-1 flex gap-1">
              {i !== 0 ? (
                <button
                  type="button"
                  title="Make primary"
                  aria-label="Make primary"
                  onClick={() => makePrimary(i)}
                  className="grid size-6 place-items-center rounded-full border border-line bg-paper text-muted hover:text-sky-700"
                >
                  <Star className="size-3" aria-hidden />
                </button>
              ) : null}
              <button
                type="button"
                title="Remove"
                aria-label="Remove image"
                onClick={() => remove(i)}
                className="grid size-6 place-items-center rounded-full border border-line bg-paper text-muted hover:text-bad"
              >
                <X className="size-3" aria-hidden />
              </button>
            </div>
          </div>
        ))}

        {Array.from({ length: emptySlots }).map((_, i) => (
          <button
            key={`slot-${i}`}
            type="button"
            onClick={upload.openPicker}
            disabled={storageOff || upload.isBusy}
            className={cn(
              "flex aspect-square flex-col items-center justify-center gap-1 rounded-sm",
              "border border-dashed border-line-2 bg-raised text-muted",
              "transition-colors hover:border-sky-600 hover:text-sky-700",
            )}
          >
            <Plus className="size-4" aria-hidden />
            <span className="text-xs">Add</span>
          </button>
        ))}
      </div>

      {/* Two inputs, one funnel: `webkitdirectory` makes a picker folder-ONLY,
          so multi-file select needs its own. React doesn't type the attribute. */}
      <input
        ref={upload.inputRef}
        className="hidden"
        {...upload.inputProps}
        onChange={(e) => {
          const picked = Array.from(e.target.files ?? []);
          e.target.value = "";
          if (picked.length) void acceptFiles(picked);
        }}
      />
      <input
        ref={folderInputRef}
        type="file"
        multiple
        className="hidden"
        disabled={upload.isBusy}
        {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
        onChange={(e) => {
          const picked = Array.from(e.target.files ?? []);
          e.target.value = "";
          if (picked.length) void acceptFiles(picked);
        }}
      />

      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          type="button"
          intent="ghost"
          size="md"
          onClick={upload.openPicker}
          disabled={full || upload.isBusy || storageOff}
        >
          <Upload className="size-4" aria-hidden />
          {upload.isBusy ? uploadingLabel(upload.status) : "Upload"}
        </Button>
        <Button
          type="button"
          intent="ghost"
          size="md"
          onClick={() => folderInputRef.current?.click()}
          disabled={full || upload.isBusy || storageOff}
        >
          <FolderUp className="size-4" aria-hidden />
          Upload folder
        </Button>
      </div>
      <p className="mt-1 text-xs text-muted">
        {value.length}/{MAX_IMAGES} added. The first image is the primary one shown on the catalog.
        {storageOff
          ? " Uploads are unavailable — object storage isn't configured for this environment."
          : " JPEG, PNG or WebP, up to 5 MB. Pick several files, or a whole folder."}
      </p>

      {/* Preview. Escape and overlay-click close come from Radix. Index-keyed
          so removing an image while it's open can't show the wrong one. */}
      <DialogRoot
        open={preview !== null && preview < value.length}
        onOpenChange={(next) => !next && setPreview(null)}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Image {(preview ?? 0) + 1} of {value.length}
              {preview === 0 ? " · primary" : ""}
            </DialogTitle>
          </DialogHeader>
          {preview !== null && value[preview] ? (
            <img
              src={value[preview]}
              alt=""
              className="max-h-[70vh] w-full rounded-sm bg-raised object-contain"
            />
          ) : null}
        </DialogContent>
      </DialogRoot>
    </div>
  );
}
